/**
 * Dashboard tab registry. The single place that knows about all 50 tabs.
 *
 * Tab kinds:
 *   - 'jobs-table': filtered job list rendered by the shared JobsTable
 *   - 'compliance' | 'playbook': shared component (×5 each)
 *   - 'custom': dedicated React component from this file's lazy imports
 *   - 'route': handled by a sibling route file (more-specific URL match wins)
 *   - 'stub': placeholder card with a note about which wave brings it back
 *
 * Dynamic tabs:
 *   Sales auto-generates a tab per active salesperson (staff with the
 *   'marketing' role). Marketing auto-generates one per distinct
 *   `referred_by` source. The shell calls dynamicTabs() and merges into
 *   the static tab list.
 */

import type { LazyExoticComponent, ComponentType } from 'react';
import { lazy } from 'react';
import { supabase } from '~/lib/supabase';

export type TabKind = 'jobs-table' | 'compliance' | 'playbook' | 'custom' | 'route' | 'stub';

export interface JobsTableSpec {
  source?: 'v_jobs_full' | 'jobs';
  eq?: Record<string, string | number | boolean | null>;
  is?: Record<string, null | boolean>;
  in?: Record<string, (string | number)[]>;
  not?: { column: string; op: string; value: string | number | boolean | null }[];
  gte?: Record<string, string | number>;
  lte?: Record<string, string | number>;
  orderBy?: { column: string; ascending?: boolean }[];
  columns: { key: string; label: string; format?: 'money' | 'date' | 'datetime' | 'division' | 'status' }[];
  /**
   * Participant columns resolved from v_job_active_participants for the visible
   * rows and injected onto each row under the `__<role>` key (e.g. a column with
   * key `__marketing` renders the marketing participant). 'supervisor' uses the
   * supervisor-or-foreman fallback. Place the matching `__<role>` column anywhere
   * in `columns` to control ordering.
   */
  participants?: ('marketing' | 'supervisor')[];
  hint?: string;
  /**
   * Adds a colored "days since" SLA column (catalog day-color bands) and sorts
   * the page OOC-first (most days first). `from` lists basis date columns,
   * first present wins; `business` (default true) counts weekdays only.
   */
  dayAging?: {
    label: string;
    from: string[];
    business?: boolean;
    thresholds: { amber: number; orange?: number; red: number };
  };
  /** Adds a "Scope" column showing field scope-of-work completion (done / items touched). */
  scopeProgress?: boolean;
  /**
   * FB65 — ONE ROW PER FAMILY. Jobs sharing a `YY-NNNN` base collapse into a
   * single entry: the base number, every division as a chip, opening the MAIN
   * job (the earliest of them). The row's other columns read off that main job.
   *
   * Opt-in, and deliberately so: the Jobs screen is the file cabinet and shows
   * files, but a dashboard tab that exists to list REC work in progress must
   * keep listing the REC jobs it filtered for. Solo jobs are unaffected either
   * way — a family of one is just the job.
   */
  familyGrouped?: boolean;
}

export interface TabDef {
  key: string;
  label: string;
  kind: TabKind;
  jobs?: JobsTableSpec;
  component?: LazyExoticComponent<ComponentType<unknown>>;
  componentWithSlug?: LazyExoticComponent<ComponentType<{ slug: string }>>;
  stubNote?: string;
  dynamic?: boolean;
  /** Routable but not shown in the tab strip (kept so old URLs keep resolving). */
  hidden?: boolean;
  /** Status on the owner's tab architecture sheet. */
  stage?: TabStage;
  /**
   * An existing tab that is NOT on the owner's sheet. Kept visible (after the
   * sheet's tabs) until the owner decides whether it stays, merges or goes —
   * owner rule: never drop an existing tab without asking.
   */
  offSheet?: boolean;
}

export interface DashboardDef {
  key: string;
  label: string;
  tabs: TabDef[];
  dynamicTabs?: () => Promise<TabDef[]>;
}

// Schedule (was the Operations dashboard, now dissolved).
const ScheduleWdp            = lazy(() => import('~/components/dashboards/operations/WorkDayPlanner').then((m) => ({ default: m.WorkDayPlanner })));
const ScheduleCapacity       = lazy(() => import('~/components/dashboards/operations/CapacityPlanner').then((m) => ({ default: m.CapacityPlanner })));
const ScheduleFleetManager   = lazy(() => import('~/components/dashboards/operations/FleetManager').then((m) => ({ default: m.FleetManager })));

// Summaries pulled up into Company when Operations / Rebuild dissolved.
const OperationsSummary      = lazy(() => import('~/components/dashboards/operations/OperationsSummary').then((m) => ({ default: m.OperationsSummary })));
const OperationsEquipment    = lazy(() => import('~/components/dashboards/operations/EquipmentTracking').then((m) => ({ default: m.EquipmentTracking })));
const RebuildSummary         = lazy(() => import('~/components/dashboards/rebuild/RebuildSummary').then((m) => ({ default: m.RebuildSummary })));

// Pending Sales moved from Operations into the (narrower) Sales dashboard.
const PendingSales           = lazy(() => import('~/components/dashboards/operations/PendingSales').then((m) => ({ default: m.PendingSales })));

const WarehouseStatus           = lazy(() => import('~/components/dashboards/warehouse/WarehouseStatus').then((m) => ({ default: m.WarehouseStatus })));
const WarehouseExpectedRevenue  = lazy(() => import('~/components/dashboards/warehouse/WarehouseExpectedRevenue').then((m) => ({ default: m.WarehouseExpectedRevenue })));
const WarehousePackoutPlanner   = lazy(() => import('~/components/dashboards/warehouse/WarehousePackoutPlanner').then((m) => ({ default: m.WarehousePackoutPlanner })));
// Inventory Management is ONE tab holding both halves: the staging board and
// the inventory system beneath it (the old separate "Inventory System" tab is
// gone). Both chunks load together so the merged tab renders in one pass.
const WarehouseInventory        = lazy(async () => {
  const [staging, system] = await Promise.all([
    import('~/components/dashboards/warehouse/WarehouseStaging'),
    import('~/components/dashboards/warehouse/WarehouseInventorySystem'),
  ]);
  const Staging = staging.WarehouseStaging;
  const System = system.WarehouseInventorySystem;
  return {
    default: () => (
      <div className="space-y-6">
        <Staging />
        <System />
      </div>
    ),
  };
});
const WarehouseLeftRack         = lazy(() => import('~/components/dashboards/warehouse/WarehouseRack').then((m) => ({ default: m.WarehouseLeftRack })));
const WarehouseRightRack        = lazy(() => import('~/components/dashboards/warehouse/WarehouseRack').then((m) => ({ default: m.WarehouseRightRack })));
const WarehouseSharedSpace      = lazy(() => import('~/components/dashboards/warehouse/WarehouseRack').then((m) => ({ default: m.WarehouseSharedSpace })));
const WarehouseRacksTab         = lazy(() => import('~/components/dashboards/warehouse/WarehouseRack').then((m) => ({ default: m.WarehouseRacksTab })));
const WarehouseLog              = lazy(() => import('~/components/dashboards/warehouse/WarehouseLog').then((m) => ({ default: m.WarehouseLog })));
const WarehouseStatusHome       = lazy(() => import('~/components/dashboards/warehouse/WarehouseStatusHome').then((m) => ({ default: m.WarehouseStatusHome })));

const CollectionsStatus      = lazy(() => import('~/components/dashboards/collections/CollectionsStatus').then((m) => ({ default: m.CollectionsStatus })));
const CollectionsCombined    = lazy(() => import('~/components/dashboards/collections/CollectionsCombined').then((m) => ({ default: m.CollectionsCombined })));

// Insurance work — these five moved INTO Collections when the standalone
// Insurance Adjusting section dissolved. Keys are unchanged so old links to
// /dashboards/insurance-adjusting/<key> land on the same tab after the redirect.
const AdjustersTab           = lazy(() => import('~/components/dashboards/insurance/directory').then((m) => ({ default: m.AdjustersTab })));
const CarriersTab            = lazy(() => import('~/components/dashboards/insurance/directory').then((m) => ({ default: m.CarriersTab })));
const CarrierInsightsTab     = lazy(() => import('~/components/dashboards/insurance/directory').then((m) => ({ default: m.CarrierInsightsTab })));
const AdjustmentAnalysis     = lazy(() => import('~/components/dashboards/collections/AdjustmentsRebuttals').then((m) => ({ default: m.AdjustmentAnalysis })));
const RebuttalAssistant      = lazy(() => import('~/components/dashboards/collections/AdjustmentsRebuttals').then((m) => ({ default: m.RebuttalAssistant })));

const SalesOverview          = lazy(() => import('~/components/dashboards/sales/SalesOverview').then((m) => ({ default: m.SalesOverview })));
const ClosingRatios          = lazy(() => import('~/components/dashboards/sales/ClosingRatios').then((m) => ({ default: m.ClosingRatios })));
const SalespersonTab         = lazy(() => import('~/components/dashboards/sales/SalespersonTab').then((m) => ({ default: m.SalespersonTab })));
const SalesCommission        = lazy(() => import('~/components/dashboards/sales/Commission').then((m) => ({ default: m.Commission })));
// Moved out of Sales into its own top-level Call Log section.
const CallAnalytics          = lazy(() => import('~/components/dashboards/sales/CallLog').then((m) => ({ default: m.CallLog })));

// Marketing: a Summary tab + one detail tab per paid lead source.
const MarketingSummary       = lazy(() => import('~/components/dashboards/marketing/MarketingSummary').then((m) => ({ default: m.MarketingSummary })));
const LeadSourceTab          = lazy(() => import('~/components/dashboards/marketing/LeadSourceTab').then((m) => ({ default: m.LeadSourceTab })));
const CustomerAnalysis       = lazy(() => import('~/components/dashboards/marketing/CustomerAnalysis').then((m) => ({ default: m.CustomerAnalysis })));
const ReviewTracker          = lazy(() => import('~/components/dashboards/marketing/ReviewTracker').then((m) => ({ default: m.ReviewTracker })));

const CompanyOverview        = lazy(() => import('~/components/dashboards/company/CompanyOverview').then((m) => ({ default: m.CompanyOverview })));
const CycleTime              = lazy(() => import('~/components/dashboards/company/CycleTime').then((m) => ({ default: m.CycleTime })));
const HrTab                  = lazy(() => import('~/components/dashboards/company/HrTab').then((m) => ({ default: m.HrTab })));
const NinetyIO               = lazy(() => import('~/components/dashboards/company/NinetyIO').then((m) => ({ default: m.NinetyIO })));
const DashboardUtilization   = lazy(() => import('~/components/dashboards/company/DashboardUtilization').then((m) => ({ default: m.DashboardUtilization })));
const FinancialsAndBudget    = lazy(() => import('~/components/dashboards/company/FinancialsAndBudget').then((m) => ({ default: m.FinancialsAndBudget })));
const Labor                  = lazy(() => import('~/components/dashboards/company/Labor').then((m) => ({ default: m.Labor })));
const CompanyBrain           = lazy(() => import('~/components/dashboards/company/CompanyBrain').then((m) => ({ default: m.CompanyBrain })));
// The Operations Job Board (same board as the Jobs page's second view).
const OperationsJobBoard     = lazy(() => import('~/components/dashboards/operations/OperationsActiveJobs').then((m) => ({ default: m.OperationsActiveJobs })));
const RebuildJobBoard        = lazy(() => import('~/components/dashboards/rebuild/RebuildJobBoard').then((m) => ({ default: m.RebuildJobBoard })));
const OrgChart               = lazy(() => import('~/components/ninety/AccountabilityChart').then((m) => ({ default: m.AccountabilityChart })));
const EmergencyAlerts        = lazy(() => import('~/components/dashboards/alert/AlertPage').then((m) => ({ default: m.AlertPage })));
const Plumbers               = lazy(() => import('~/components/dashboards/plumber/PlumberManager').then((m) => ({ default: m.PlumberManager })));
// The referral map on its own, wrapped in the plumber data provider it reads from.
const PlumberReferralMap     = lazy(async () => {
  const [store, map] = await Promise.all([
    import('~/components/dashboards/plumber/plumberStore'),
    import('~/components/dashboards/plumber/ReferralMapTab'),
  ]);
  const Provider = store.PlumberDataProvider;
  const Map = map.ReferralMapTab;
  return { default: () => <Provider><Map /></Provider> };
});

/**
 * Status of each tab on the owner's "PDQ OS — Tab Architecture" sheet (9/24).
 * Colour legend there: New · In progress · Dependency · Done · Not confirmed
 * to create just yet.
 */
export type TabStage = 'new' | 'in-progress' | 'dependency' | 'done' | 'not-confirmed';

export const STAGE_LABEL: Record<TabStage, string> = {
  'new': 'New',
  'in-progress': 'In progress',
  'dependency': 'Dependency',
  'done': 'Done',
  'not-confirmed': 'Not confirmed to create just yet',
};

/** A tab from the sheet that has no screen yet — placeholder card until the owner specs it. */
function planned(key: string, label: string, stage: TabStage): TabDef {
  return {
    key, label, kind: 'stub', stage,
    stubNote: `${label} is on the tab architecture sheet (status: ${STAGE_LABEL[stage]}). `
      + 'This screen will be designed from the owner’s walkthrough, one tab at a time.',
  };
}

export function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}


/*
 * Sub-tabs follow the owner's "PDQ OS — Tab Architecture" sheet (9/24). Where a
 * sheet tab matches a screen we already had, that screen is reused; the rest
 * are `planned()` placeholders. Existing tabs the sheet doesn't list are kept
 * (marked `offSheet`, listed last) until the owner decides on each one.
 */
export const DASHBOARDS: Record<string, DashboardDef> = {
  operations: {
    key: 'operations', label: 'Operations',
    tabs: [
      { key: 'summary',            label: 'Ops Summary',        kind: 'custom', component: OperationsSummary, stage: 'in-progress' },
      { key: 'job-board',          label: 'Operations Job Board', kind: 'custom', component: OperationsJobBoard, stage: 'done' },
      { key: 'schedule',           label: 'Schedule',           kind: 'custom', component: ScheduleWdp, stage: 'in-progress' },
      { key: 'capacity',           label: 'Capacity Planner',   kind: 'custom', component: ScheduleCapacity, stage: 'in-progress' },
      { key: 'fleet-manager',      label: 'Fleet Manager',      kind: 'custom', component: ScheduleFleetManager, stage: 'in-progress' },
      planned('facility-inventory', 'Facility & Inventory', 'not-confirmed'),
      { key: 'dispatch',           label: 'Dispatch',           kind: 'route', offSheet: true },
      { key: 'equipment-tracking', label: 'Equipment Tracking', kind: 'custom', component: OperationsEquipment, offSheet: true },
    ],
  },

  rebuild: {
    key: 'rebuild', label: 'Rebuild',
    tabs: [
      { key: 'summary', label: 'Rebuild Summary & Schedule', kind: 'custom', component: RebuildSummary, stage: 'new' },
      { key: 'job-board', label: 'Rebuild Job Board', kind: 'custom', component: RebuildJobBoard, stage: 'in-progress' },
      planned('work-order-manager', 'Work Order Manager', 'not-confirmed'),
    ],
  },

  sales: {
    key: 'sales', label: 'Sales',
    tabs: [
      { key: 'summary',              label: 'Summary',              kind: 'custom', component: SalesOverview, stage: 'new' },
      planned('live-leads', 'Live Leads and Stats', 'not-confirmed'),
      { key: 'closing-ratios',       label: 'Closing Ratios',       kind: 'custom', component: ClosingRatios, stage: 'new' },
      { key: 'commission',           label: 'Commission',           kind: 'custom', component: SalesCommission, stage: 'new' },
      { key: 'emergency-alerts',     label: 'Emergency Alerts',     kind: 'custom', component: EmergencyAlerts, stage: 'new' },
      { key: 'plumbers',             label: 'Plumbers',             kind: 'custom', component: Plumbers, stage: 'new' },
      { key: 'plumber-referral-map', label: 'Plumber Referral Map', kind: 'custom', component: PlumberReferralMap, stage: 'not-confirmed' },
      { key: 'pending-sales',        label: 'Pending Sales',        kind: 'custom', component: PendingSales, offSheet: true },
    ],
    // Per-salesperson tabs (not on the sheet — awaiting the owner's decision).
    dynamicTabs: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('user_id, display_name, username, staff_role_keys')
        .eq('is_active', true);
      // Owner request: only these two salespeople get their own tab.
      const KEEP_SALESPEOPLE = ['michael moughrabie', 'hunter halliburton'];
      const sales = (data ?? []).filter((p) =>
        KEEP_SALESPEOPLE.includes((p.display_name ?? '').trim().toLowerCase()));
      return sales.map((p) => ({
        key: p.username || slugify(p.display_name),
        label: p.display_name,
        kind: 'custom' as const,
        componentWithSlug: SalespersonTab,
        dynamic: true,
        offSheet: true,
      }));
    },
  },

  // Key stays 'call-log' so existing /dashboards/call-log links keep working.
  'call-log': {
    key: 'call-log', label: 'Calls',
    tabs: [
      // The CallLog component is the call list (it was labelled "Call Analytics").
      { key: 'call-log', label: 'Call Log', kind: 'custom', component: CallAnalytics, stage: 'in-progress' },
      planned('call-analytics', 'Call Analytics', 'new'),
      planned('incoming-calls', 'Incoming Calls', 'not-confirmed'),
    ],
  },

  collections: {
    key: 'collections', label: 'Collections',
    tabs: [
      { key: 'company-status',      label: 'Summary',             kind: 'custom', component: CollectionsStatus, stage: 'new' },
      // ONE combined view, organised by collector with filters. The
      // 45-business-day handoff rule still comes from isMeredithRow/isSueRow.
      { key: 'collections',         label: 'Collections',         kind: 'custom', component: CollectionsCombined, stage: 'new' },
      planned('insurance', 'Insurance', 'new'),
      { key: 'rebuttal-assistant',  label: 'Rebuttal Assistant',  kind: 'custom', component: RebuttalAssistant, stage: 'not-confirmed' },
      { key: 'adjustment-analysis', label: 'Adjustment Analysis', kind: 'custom', component: AdjustmentAnalysis, stage: 'not-confirmed' },
      // Not on the sheet — close to Insurance; awaiting the owner's decision.
      { key: 'adjusters', label: 'Adjuster Directory', kind: 'custom', component: AdjustersTab, offSheet: true },
      { key: 'carriers',  label: 'Carrier Directory',  kind: 'custom', component: CarriersTab, offSheet: true },
      { key: 'insight',   label: 'Carrier Insights',   kind: 'custom', component: CarrierInsightsTab, offSheet: true },
    ],
  },

  warehouse: {
    key: 'warehouse', label: 'Warehouse',
    tabs: [
      // Label is 'Summary' but the key stays 'status' so existing routes/links don't break.
      { key: 'status',           label: 'Summary',              kind: 'custom', component: WarehouseStatus, stage: 'new' },
      { key: 'warehouse-status', label: 'Warehouse Status',     kind: 'custom', component: WarehouseStatusHome, stage: 'new' },
      // One tab holding both the staging board and the inventory system.
      { key: 'inventory',        label: 'Inventory Management', kind: 'custom', component: WarehouseInventory, stage: 'new' },
      { key: 'warehouse-log',    label: 'Warehouse Log',        kind: 'custom', component: WarehouseLog, stage: 'new' },
      // One Racks tab (Left / Right / Shared picker inside); the three old keys
      // stay routable but hidden so bookmarks and TV links keep working.
      { key: 'racks',            label: 'Racks',                kind: 'custom', component: WarehouseRacksTab, stage: 'new' },
      { key: 'expected-revenue', label: 'Expected Revenue',     kind: 'custom', component: WarehouseExpectedRevenue, stage: 'not-confirmed' },
      { key: 'left-rack',        label: 'Left Rack',            kind: 'custom', component: WarehouseLeftRack,    hidden: true },
      { key: 'right-rack',       label: 'Right Rack',           kind: 'custom', component: WarehouseRightRack,   hidden: true },
      { key: 'shared-space',     label: 'Shared Space',         kind: 'custom', component: WarehouseSharedSpace, hidden: true },
      { key: 'packout-planner',  label: 'Packout Planner',      kind: 'custom', component: WarehousePackoutPlanner, offSheet: true },
    ],
  },

  marketing: {
    key: 'marketing', label: 'Marketing',
    tabs: [
      { key: 'summary',               label: 'Summary',           kind: 'custom', component: MarketingSummary, stage: 'new' },
      planned('lead-source-tracking', 'Lead Source Tracking', 'new'),
      { key: 'customer-analysis',     label: 'Customer Analysis', kind: 'custom', component: CustomerAnalysis, stage: 'new' },
      { key: 'google-review-tracker', label: 'Review Tracker',    kind: 'custom', component: ReviewTracker, stage: 'new' },
      // Not on the sheet — close to Lead Source Tracking; awaiting the owner's decision.
      { key: 'service-direct', label: 'Service Direct', kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
      { key: 'inquirly',       label: 'Inquirly',       kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
      { key: '33-mile',        label: '33 Mile',        kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
      { key: 'elocal',         label: 'eLocal',         kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
      { key: 'ppc',            label: 'PPC',            kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
      { key: 'lsa',            label: 'LSA',            kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
      { key: 'seo',            label: 'SEO',            kind: 'custom', componentWithSlug: LeadSourceTab, offSheet: true },
    ],
  },

  brain: {
    key: 'brain', label: 'Brain',
    tabs: [
      { key: 'brain', label: 'Brain', kind: 'custom', component: CompanyBrain, stage: 'not-confirmed' },
    ],
  },

  'company-overview': {
    key: 'company-overview', label: 'Company',
    tabs: [
      { key: 'overview',   label: 'Company Status', kind: 'custom', component: CompanyOverview, stage: 'new' },
      { key: 'ninety-io',  label: 'Measurables',    kind: 'custom', component: NinetyIO, stage: 'new' },
      { key: 'financials', label: 'Financials',     kind: 'custom', component: FinancialsAndBudget, stage: 'new' },
      { key: 'labor',      label: 'Labor',          kind: 'custom', component: Labor, stage: 'new' },
      { key: 'cycle-time', label: 'Cycle Time',     kind: 'custom', component: CycleTime, stage: 'new' },
      // Moved here from My Day (9/24 sheet).
      { key: 'org-chart',  label: 'Org Chart',      kind: 'custom', component: OrgChart, stage: 'in-progress' },
      // Not on the sheet — awaiting the owner's decision.
      { key: 'operations-summary',    label: 'Operations Summary',    kind: 'custom', component: OperationsSummary, offSheet: true },
      { key: 'rebuild-summary',       label: 'Rebuild Summary',       kind: 'custom', component: RebuildSummary, offSheet: true },
      { key: 'sales-summary',         label: 'Sales Summary',         kind: 'custom', component: SalesOverview, offSheet: true },
      { key: 'equipment-tracking',    label: 'Equipment Tracking',    kind: 'custom', component: OperationsEquipment, offSheet: true },
      { key: 'brain',                 label: 'Company Brain',         kind: 'custom', component: CompanyBrain, offSheet: true },
      { key: 'commission',            label: 'Commission',            kind: 'custom', component: SalesCommission, offSheet: true },
      { key: 'hr',                    label: 'HR',                    kind: 'custom', component: HrTab, offSheet: true },
      { key: 'dashboard-utilization', label: 'Dashboard Utilization', kind: 'custom', component: DashboardUtilization, offSheet: true },
    ],
  },
};

export function getTab(dashboardKey: string, tabKey: string): { dashboard: DashboardDef; tab: TabDef } | null {
  const dashboard = DASHBOARDS[dashboardKey];
  if (!dashboard) return null;
  const tab = dashboard.tabs.find((t) => t.key === tabKey);
  if (!tab) return null;
  return { dashboard, tab };
}
